import idx from 'idx'
import axios from 'axios'
import {addDays} from 'date-fns'
import {normalize} from 'normalizr'
import {API_URL, SAUCE_API_URL} from 'config'
import {push} from 'connected-react-router'

import {dataURLtoFile} from 'utils/files'
import initialState from 'reducers/initialState'
import errorDispatcher from 'utils/errorDispatcher'
import {organisationSchema} from 'modules/schema'
import {getAuthorizationHeader} from 'utils/session'
import {populateUuid, populateUuids} from "utils/api";

// Actions
export const SELECT_ORGANISATION = 'SELECT_ORGANISATION'
export const API_GET_ORGANISATIONS_REQUEST = 'API_GET_ORGANISATIONS_REQUEST'
export const API_GET_ORGANISATIONS_SUCCESS = 'API_GET_ORGANISATIONS_SUCCESS'
export const API_GET_ORGANISATIONS_ERROR = 'API_GET_ORGANISATIONS_ERROR'
export const API_CREATE_ORGANISATION_REQUEST = 'API_CREATE_ORGANISATION_REQUEST'
export const API_CREATE_ORGANISATION_SUCCESS = 'API_CREATE_ORGANISATION_SUCCESS'
export const API_CREATE_ORGANISATION_ERROR = 'API_CREATE_ORGANISATION_ERROR'
export const API_UPDATE_ORGANISATION_REQUEST = 'API_UPDATE_ORGANISATION_REQUEST'
export const API_UPDATE_ORGANISATION_SUCCESS = 'API_UPDATE_ORGANISATION_SUCCESS'
export const API_UPDATE_ORGANISATION_ERROR = 'API_UPDATE_ORGANISATION_ERROR'
export const API_UPDATE_ORGANISATION_IMAGE_SUCCESS = 'API_UPDATE_ORGANISATION_IMAGE_SUCCESS'
export const API_UPDATE_ORGANISATION_IMAGE_REQUEST = 'API_UPDATE_ORGANISATION_IMAGE_REQUEST'
export const API_UPDATE_ORGANISATION_IMAGE_ERROR = 'API_UPDATE_ORGANISATION_IMAGE_ERROR'
export const API_DELETE_ORGANISATION_IMAGE_SUCCESS = 'API_DELETE_ORGANISATION_IMAGE_SUCCESS'
export const API_DELETE_ORGANISATION_IMAGE_REQUEST = 'API_DELETE_ORGANISATION_IMAGE_REQUEST'
export const API_DELETE_ORGANISATION_IMAGE_ERROR = 'API_DELETE_ORGANISATION_IMAGE_ERROR'

export const API_GET_ORGANISATION_CODE_REQUEST = 'API_GET_ORGANISATION_CODE_REQUEST'
export const API_GET_ORGANISATION_CODE_SUCCESS = 'API_GET_ORGANISATION_CODE_SUCCESS'
export const API_GET_ORGANISATION_CODE_ERROR = 'API_GET_ORGANISATION_CODE_ERROR'
export const API_CREATE_ORGANISATION_CODE_REQUEST = 'API_CREATE_ORGANISATION_CODE_REQUEST'
export const API_CREATE_ORGANISATION_CODE_SUCCESS = 'API_CREATE_ORGANISATION_CODE_SUCCESS'
export const API_CREATE_ORGANISATION_CODE_ERROR = 'API_CREATE_ORGANISATION_CODE_ERROR'
export const API_DELETE_ORGANISATION_CODE_REQUEST = 'API_DELETE_ORGANISATION_CODE_REQUEST'
export const API_DELETE_ORGANISATION_CODE_SUCCESS = 'API_DELETE_ORGANISATION_CODE_SUCCESS'
export const API_DELETE_ORGANISATION_CODE_ERROR = 'API_DELETE_ORGANISATION_CODE_ERROR'
export const API_CREATE_GROUP_INVITATION_REQUEST = 'API_CREATE_GROUP_INVITATION_REQUEST'
export const API_CREATE_GROUP_INVITATION_SUCCESS = 'API_CREATE_GROUP_INVITATION_SUCCESS'
export const API_CREATE_GROUP_INVITATION_ERROR = 'API_CREATE_GROUP_INVITATION_ERROR'

// Reducer
export default function reducer(state = initialState.organisation, action) {
    switch (action.type) {
        case SELECT_ORGANISATION:
            return {
                ...state,
                selected: action.organisation
            }
        case API_GET_ORGANISATIONS_REQUEST:
            return {
                ...state,
                items: null,
            }
        case API_GET_ORGANISATIONS_SUCCESS:
            return {
                ...state,
                items: action.organisations
            }
        case API_CREATE_ORGANISATION_SUCCESS:
            return {
                ...state,
                items: {
                    ...state.items,
                    [action.organisation.uuid]: action.organisation
                }
            }
        case API_UPDATE_ORGANISATION_SUCCESS:
        case API_UPDATE_ORGANISATION_IMAGE_SUCCESS:
        case API_DELETE_ORGANISATION_IMAGE_SUCCESS:
            return {
                ...state,
                selected: action.updatedOrganisation
            }
        case API_GET_ORGANISATION_CODE_SUCCESS:
            return {
                ...state,
                selected: {
                    ...state.selected,
                    code: action.code
                }
            }
        case API_CREATE_ORGANISATION_CODE_SUCCESS:
            return {
                ...state,
                selected: {
                    ...state.selected,
                    code: action.code
                }
            }
        case API_DELETE_ORGANISATION_CODE_SUCCESS:
            return {
                ...state,
                selected: {
                    ...state.selected,
                    code: null
                }
            }
        default:
            return state
    }
}

// Action Creators
export function getOrganisations() {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_ORGANISATIONS_REQUEST})

        try {
            const response = await axios.get(`${API_URL}/projects`, getAuthorizationHeader())
            const raw = populateUuids(response.data)
            const {entities: {organisations}} = normalize(raw, [organisationSchema])
            dispatch({type: API_GET_ORGANISATIONS_SUCCESS, organisations: organisations || {}})
        }
        catch (error) {
            console.error("/projects failed", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_GET_ORGANISATIONS_ERROR, dispatch)
        }
    }
}

export function createOrganisation(form) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_CREATE_ORGANISATION_REQUEST})
        /*
        // eslint-disable-next-line
        const formData = new FormData()

        formData.set('name', form.name)
        // only append image if an image has been added.
        if (form.image !== null) {
            const imageFile = dataURLtoFile(form.image.src, 'org-logo.png')
            formData.append('file', imageFile)
        }
        */
        const formData = {name: form.name}

        try {
            const response = await axios.post(`${API_URL}/project/create`, formData, getAuthorizationHeader())
            dispatch({type: API_CREATE_ORGANISATION_SUCCESS, organisation: populateUuid(response.data)})
            dispatch(push('/account/home'))
        }
        catch (error) {
            dispatch({type: API_CREATE_ORGANISATION_ERROR, error})
        }
    }
}

export function updateOrganisation(organisation, form, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_UPDATE_ORGANISATION_REQUEST})

        try {
            const response = await axios.put(
                `${API_URL}/project/${organisation.uuid}`,
                {name: form.name},
                getAuthorizationHeader()
            )
            dispatch({type: API_UPDATE_ORGANISATION_SUCCESS, updatedOrganisation: populateUuid(response.data)})
            if (callback) {
                dispatch(callback)
            }
        }
        catch (error) {
            dispatch({type: API_UPDATE_ORGANISATION_ERROR, error})
        }
    }
}

export function updateOrganisationImage(organisation, image, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_UPDATE_ORGANISATION_IMAGE_REQUEST})
        // eslint-disable-next-line
        const formData = new FormData()
        const imageFile = dataURLtoFile(image.src, 'org-logo.png')

        formData.append('file', imageFile)

        try {
            await axios.put(
                `${SAUCE_API_URL}/organisation/${organisation.uuid}/image`,
                formData,
                getAuthorizationHeader()
            )
                .then((result) => {
                    const updatedOrganisation = idx(result, _ => _.data.data.rows[0]) || {}
                    dispatch({type: API_UPDATE_ORGANISATION_IMAGE_SUCCESS, updatedOrganisation})
                    if (callback) {
                        dispatch(callback)
                    }
                }).catch((error) => {
                    return Promise.reject(error)
                })
        }
        catch (error) {
            dispatch({type: API_UPDATE_ORGANISATION_IMAGE_ERROR, error})
        }
    }
}

export function deleteOrganisationImage(organisation, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DELETE_ORGANISATION_IMAGE_REQUEST})

        try {
            await axios.delete(
                `${SAUCE_API_URL}/organisation/${organisation.uuid}/image`,
                getAuthorizationHeader()
            )
                .then((result) => {
                    const updatedOrganisation = idx(result, _ => _.data.data.rows[0]) || {}
                    dispatch({type: API_DELETE_ORGANISATION_IMAGE_SUCCESS, updatedOrganisation})
                    if (callback) {
                        dispatch(callback)
                    }
                }).catch((error) => {
                    return Promise.reject(error)
                })
        }
        catch (error) {
            dispatch({type: API_DELETE_ORGANISATION_IMAGE_ERROR, error})
        }
    }
}

export function getOrganisationCode(organisation) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_ORGANISATION_CODE_REQUEST})

        try {
            const response = await axios.get(`${API_URL}/project/${organisation.uuid}/invitation-code`, getAuthorizationHeader())
            dispatch({type: API_GET_ORGANISATION_CODE_SUCCESS, code: response.data})
        }
        catch (error) {
            dispatch({type: API_GET_ORGANISATION_CODE_ERROR, error})
        }
    }
}

export function createOrganisationCode(organisation, expireDate, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_CREATE_ORGANISATION_CODE_REQUEST})

        const body = {
            expiresAt: expireDate
        }

        try {
            await axios.post(`${API_URL}/project/${organisation.uuid}/create-invitation`, body, getAuthorizationHeader()).then((result) => {
                const code = {
                    expireDate,
                    code: result.data.code
                }
                dispatch({type: API_CREATE_ORGANISATION_CODE_SUCCESS, code})

                if (callback) {
                    dispatch(callback)
                }
            })
                .catch((error) => {
                    Promise.reject(error.response)
                })
        }
        catch (error) {
            dispatch({type: API_CREATE_ORGANISATION_CODE_ERROR})
        }

    }
}

export function deleteOrganisationCode(organisation) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DELETE_ORGANISATION_CODE_REQUEST})
        try {
            await axios.put(
                `${API_URL}/project/${organisation.uuid}/deactivate-invitation`, null, getAuthorizationHeader()
            ).then(() => {
                dispatch({type: API_DELETE_ORGANISATION_CODE_SUCCESS})
            }).catch((error) => {
                return Promise.reject(error.response)
            })
        }
        catch (error) {
            dispatch({type: API_DELETE_ORGANISATION_CODE_ERROR, error})
        }
    }
}

export function sendAdminInvitation(organisation, person, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_CREATE_GROUP_INVITATION_REQUEST})

        try {
            const body = {
                email: person.email,
            }
            await axios.post(`${API_URL}/project/${organisation.uuid}/create-admin-invitation`, body, getAuthorizationHeader())
            dispatch({type: API_CREATE_GROUP_INVITATION_SUCCESS})

            if (callback) {
                dispatch(callback)
            }
        }
        catch (error) {
            console.error("sendAdminInvitation", error)
            dispatch({type: API_CREATE_GROUP_INVITATION_ERROR, error})
        }
    }
}

export function selectOrganisation(organisation) {
    return async function dispatcher(dispatch) {
        dispatch({type: SELECT_ORGANISATION, organisation})
        dispatch(push('/dashboard/overview'))
    }
}



// WEBPACK FOOTER //
// ./src/modules/organisation.js